import {
  AccountApiError,
  type AccountApiErrorContext,
  isRecord,
  requestAccountJson
} from "./account-api";

export type NoteEntityType = "ticker" | "issuer" | "owner" | "cluster";

export interface EntityNote {
  id: string;
  entityType: NoteEntityType;
  entityId: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export class NotesApiError extends AccountApiError {
  constructor(code: string, message: string, context: AccountApiErrorContext) {
    super(code, message, context);
    this.name = "NotesApiError";
  }
}

const createError = (code: string, message: string, context: AccountApiErrorContext) =>
  new NotesApiError(code, message, context);

function parseNote(value: unknown, endpoint: string): EntityNote {
  if (
    !isRecord(value) ||
    typeof value.id !== "string" ||
    typeof value.entityType !== "string" ||
    typeof value.entityId !== "string" ||
    typeof value.body !== "string" ||
    typeof value.createdAt !== "string" ||
    typeof value.updatedAt !== "string"
  ) {
    throw new NotesApiError("notes_invalid", "Note response has an invalid shape", { endpoint });
  }
  return {
    id: value.id,
    entityType: value.entityType as NoteEntityType,
    entityId: value.entityId,
    body: value.body,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt
  };
}

export async function loadNotes(params?: {
  entityType?: NoteEntityType;
  entityId?: string;
}): Promise<EntityNote[]> {
  const search = new URLSearchParams();
  if (params?.entityType) search.set("entityType", params.entityType);
  if (params?.entityId) search.set("entityId", params.entityId);
  const endpoint = `/api/notes${search.toString() ? `?${search}` : ""}`;
  const payload = await requestAccountJson({
    endpoint,
    unavailableCode: "notes_unavailable",
    httpCode: "notes_http",
    invalidCode: "notes_invalid",
    createError
  });
  if (!isRecord(payload) || !Array.isArray(payload.notes)) {
    throw new NotesApiError("notes_invalid", "Notes response has an invalid shape", { endpoint });
  }
  return payload.notes.map((item) => parseNote(item, endpoint));
}

export async function createNote(input: {
  entityType: NoteEntityType;
  entityId: string;
  body: string;
}): Promise<EntityNote> {
  const endpoint = "/api/notes";
  const payload = await requestAccountJson({
    endpoint,
    init: {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input)
    },
    unavailableCode: "notes_unavailable",
    httpCode: "notes_http",
    invalidCode: "notes_invalid",
    createError
  });
  if (!isRecord(payload)) {
    throw new NotesApiError("notes_invalid", "Create note response invalid", { endpoint });
  }
  return parseNote(payload.note, endpoint);
}

export async function deleteNote(id: string): Promise<void> {
  const endpoint = `/api/notes/${encodeURIComponent(id)}`;
  await requestAccountJson({
    endpoint,
    init: { method: "DELETE" },
    unavailableCode: "notes_unavailable",
    httpCode: "notes_http",
    invalidCode: "notes_invalid",
    createError
  });
}
